import { useRef, useState } from 'react';
import { CheckCircle2, FileSpreadsheet, LockKeyhole, UploadCloud, X } from 'lucide-react';
import type { ImportProgress } from '../lib/excel';

interface UploadModalProps {
  open: boolean;
  progress: ImportProgress | null;
  error: string;
  onClose: () => void;
  onFile: (file: File) => void;
}

export default function UploadModal({ open, progress, error, onClose, onFile }: UploadModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  if (!open) return null;

  const choose = (file?: File) => {
    if (file) onFile(file);
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !progress && onClose()}>
      <section className="modal upload-modal" role="dialog" aria-modal="true" aria-labelledby="upload-title">
        <button className="icon-button modal-close" type="button" onClick={onClose} disabled={Boolean(progress)} aria-label="关闭">
          <X size={18} />
        </button>
        <div className="modal-kicker"><FileSpreadsheet size={16} /> 每周数据更新</div>
        <h2 id="upload-title">上传销售基础数据</h2>
        <p className="modal-description">系统会自动寻找表头并识别产品、金额、销售时间、终端与区域等字段。</p>
        <button
          type="button"
          className={`drop-zone ${dragging ? 'is-dragging' : ''}`}
          onClick={() => !progress && inputRef.current?.click()}
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            choose(event.dataTransfer.files[0]);
          }}
          disabled={Boolean(progress)}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xlsm,.xls"
            hidden
            onChange={(event) => choose(event.target.files?.[0])}
          />
          {progress ? (
            <>
              <div className="progress-orbit"><span /></div>
              <strong>{progress.message}</strong>
              <span>{progress.progress}%</span>
              <div className="upload-progress"><i style={{ width: `${progress.progress}%` }} /></div>
            </>
          ) : (
            <>
              <span className="drop-icon"><UploadCloud size={30} /></span>
              <strong>拖入 Excel，或点击选择文件</strong>
              <span>支持 .xlsx / .xlsm / .xls，最大 60 MB</span>
            </>
          )}
        </button>
        {error && <div className="form-error" role="alert">{error}</div>}
        <div className="upload-benefits">
          <div><CheckCircle2 size={16} /><span>自动识别中文字段</span></div>
          <div><CheckCircle2 size={16} /><span>覆盖旧数据前先完成校验</span></div>
          <div><LockKeyhole size={16} /><span>原始数据不上传服务器</span></div>
        </div>
      </section>
    </div>
  );
}
